import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';

// Simplified constellation background
const ConstellationBackground = ({ theme }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nodesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = document.documentElement.scrollHeight;

    const resize = () => {
      width = window.innerWidth;
      height = Math.max(document.documentElement.scrollHeight, window.innerHeight);
      canvas.width = width;
      canvas.height = height;
      initNodes();
    };

    const initNodes = () => {
      const nodeCount = Math.floor((width * height) / 25000);
      nodesRef.current = [];
      for (let i = 0; i < nodeCount; i++) {
        nodesRef.current.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.15,
          vy: (Math.random() - 0.5) * 0.15,
          radius: Math.random() * 1.5 + 0.5,
          pulse: Math.random() * Math.PI * 2,
        });
      }
    };

    const draw = () => {
      const isDark = theme === 'dark';
      ctx.fillStyle = isDark
        ? 'rgba(10, 22, 40, 0.03)'
        : 'rgba(239, 246, 255, 0.03)';
      ctx.fillRect(0, 0, width, height);

      const nodeColor = isDark ? 'rgba(147, 197, 253, ' : 'rgba(59, 130, 246, ';
      const lineColor = isDark ? 'rgba(147, 197, 253, ' : 'rgba(59, 130, 246, ';

      nodesRef.current.forEach((node, i) => {
        node.x += node.vx;
        node.y += node.vy;
        node.pulse += 0.01;

        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;

        const pulseScale = 1 + Math.sin(node.pulse) * 0.2;
        const radius = node.radius * pulseScale;

        ctx.beginPath();
        ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = nodeColor + (0.3 + Math.sin(node.pulse) * 0.1) + ')';
        ctx.fill();

        nodesRef.current.slice(i + 1).forEach(other => {
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = lineColor + (0.08 * (1 - dist / 120)) + ')';
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationRef.current);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

// FAQ Item component
const FAQItem = ({ question, answer, delay = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="animate-fade-in"
      style={{ animationDelay: `${delay}ms` }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left p-5 bg-white/70 dark:bg-navy-800/50 backdrop-blur-xl rounded-2xl border border-silver-200/50 dark:border-silver-700/20 hover:border-alpha-300 dark:hover:border-alpha-600/30 transition-all duration-300 group"
      >
        <div className="flex items-center justify-between gap-4">
          <h3 className="font-bold text-silver-800 dark:text-white group-hover:text-alpha-600 dark:group-hover:text-alpha-400 transition-colors">
            {question}
          </h3>
          <span className={`flex-shrink-0 w-8 h-8 rounded-lg bg-silver-100 dark:bg-navy-700 flex items-center justify-center transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
            <svg className="w-4 h-4 text-silver-500 dark:text-silver-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>
        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-40 mt-3' : 'max-h-0'}`}>
          <p className="text-silver-600 dark:text-silver-400 text-sm leading-relaxed">
            {answer}
          </p>
        </div>
      </button>
    </div>
  );
};

const Support = ({ theme }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const emailBody = `Name: ${formData.name}\n\nMessage:\n${formData.message}`;
    const mailtoLink = `mailto:support@woleh.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(emailBody)}`;

    window.location.href = mailtoLink;

    setTimeout(() => {
      setFormData({ name: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const faqs = [
    {
      question: 'How do I upgrade to Pro?',
      answer: 'Download the HeyAlphas app from the App Store or Google Play, then navigate to Settings and tap on "Upgrade to Pro" to enjoy an ad-free experience with priority support and early access to new features.'
    },
    {
      question: 'How do I cancel my subscription?',
      answer: 'You can manage your subscription through your device\'s app store settings. On iOS, go to Settings > Apple ID > Subscriptions. On Android, open the Google Play Store > Menu > Subscriptions.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes, we use industry-standard encryption for all data in transit and at rest. Payment processing is handled securely through Stripe and Apple, and we never store your complete payment information.'
    },
    {
      question: 'How do I add coins to my watchlist?',
      answer: 'Simply tap on any coin card and click the "Add to Watchlist" button. All users have unlimited watchlist capacity to track as many coins as they want.'
    },
    {
      question: 'What are AI Insights?',
      answer: 'AI Insights is powered by our AI assistant Alphie, who analyzes market patterns, sentiment, and on-chain data to surface high-potential opportunities before they trend. This feature is available to all users.'
    }
  ];

  return (
    <div className={`relative min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-alpha-50 via-white to-silver-100 dark:from-navy-950 dark:via-navy-900 dark:to-alpha-950 transition-colors duration-500" />
      <ConstellationBackground theme={theme} />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 dark:bg-navy-900/80 backdrop-blur-xl border-b border-silver-200/50 dark:border-silver-700/20">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-silver-600 dark:text-silver-400 hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="HeyAlphas" className="w-8 h-8" />
            <span className="text-xl font-black tracking-tight text-silver-800 dark:text-white">
              Hey<span className="text-alpha-500">Alphas</span>
            </span>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12 animate-slide-up">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-alpha-gradient shadow-alpha-lg mb-6">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-silver-800 dark:text-white mb-3">
            How can we <span className="text-alpha-500">help</span>?
          </h1>
          <p className="text-xl text-silver-500 dark:text-silver-400 max-w-2xl mx-auto">
            We're here to help you get the most out of HeyAlphas
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="animate-fade-in" style={{ animationDelay: '100ms' }}>
            <div className="relative bg-white/70 dark:bg-navy-800/50 backdrop-blur-xl rounded-3xl border border-silver-200/50 dark:border-silver-700/20 p-8 shadow-xl">
              {/* Decorative corner */}
              <div className="absolute top-6 right-6 w-12 h-12 border-t-2 border-r-2 border-alpha-300/30 dark:border-alpha-500/20 rounded-tr-2xl" />

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-alpha-gradient flex items-center justify-center shadow-alpha">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-silver-800 dark:text-white">
                  Send us a message
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-silver-700 dark:text-silver-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full px-4 py-3 bg-white/80 dark:bg-navy-700/50 border border-silver-200 dark:border-silver-700/50 rounded-xl text-silver-800 dark:text-white placeholder-silver-400 dark:placeholder-silver-500 focus:outline-none focus:ring-2 focus:ring-alpha-500/50 focus:border-alpha-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-silver-700 dark:text-silver-300 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="How can we help?"
                    className="w-full px-4 py-3 bg-white/80 dark:bg-navy-700/50 border border-silver-200 dark:border-silver-700/50 rounded-xl text-silver-800 dark:text-white placeholder-silver-400 dark:placeholder-silver-500 focus:outline-none focus:ring-2 focus:ring-alpha-500/50 focus:border-alpha-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-silver-700 dark:text-silver-300 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell us more about your question or issue..."
                    className="w-full px-4 py-3 bg-white/80 dark:bg-navy-700/50 border border-silver-200 dark:border-silver-700/50 rounded-xl text-silver-800 dark:text-white placeholder-silver-400 dark:placeholder-silver-500 focus:outline-none focus:ring-2 focus:ring-alpha-500/50 focus:border-alpha-500 transition-all resize-none"
                  />
                </div>

                <div className="p-4 bg-alpha-50/50 dark:bg-alpha-900/20 rounded-xl border border-alpha-200/50 dark:border-alpha-700/30">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-alpha-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-silver-600 dark:text-silver-400">
                      Clicking "Send Message" will open your email client with the message pre-filled.
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 px-6 bg-alpha-gradient text-white font-bold rounded-xl shadow-alpha hover:shadow-alpha-lg transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:transform-none"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Opening Email...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info Card */}
            <div className="mt-6 bg-white/70 dark:bg-navy-800/50 backdrop-blur-xl rounded-2xl border border-silver-200/50 dark:border-silver-700/20 p-6">
              <h3 className="font-bold text-silver-800 dark:text-white mb-4">
                Other Ways to Reach Us
              </h3>
              <div className="space-y-3">
                <a
                  href="mailto:support@woleh.com"
                  className="flex items-center gap-3 text-silver-600 dark:text-silver-400 hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-silver-100 dark:bg-navy-700 flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  support@woleh.com
                </a>
                <a
                  href="https://x.com/heyalphas_"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-silver-600 dark:text-silver-400 hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-silver-100 dark:bg-navy-700 flex items-center justify-center">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </div>
                  @heyalphas_
                </a>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div>
            <div className="flex items-center gap-3 mb-6 animate-fade-in">
              <div className="w-10 h-10 rounded-xl bg-alpha-gradient flex items-center justify-center shadow-alpha">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-silver-800 dark:text-white">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  delay={150 + index * 50}
                />
              ))}
            </div>

            {/* App Download CTA */}
            <div className="mt-8 bg-gradient-to-br from-alpha-500/10 to-alpha-600/10 dark:from-alpha-500/20 dark:to-alpha-600/20 rounded-2xl border border-alpha-300/30 dark:border-alpha-500/20 p-6 animate-fade-in" style={{ animationDelay: '500ms' }}>
              <h3 className="font-bold text-silver-800 dark:text-white mb-2">
                Get the App
              </h3>
              <p className="text-sm text-silver-600 dark:text-silver-400 mb-4">
                Download HeyAlphas on your mobile device for the best experience.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://apps.apple.com/gb/app/heyalphas/id6748565686"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-silver-900 dark:bg-white text-white dark:text-silver-900 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  App Store
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.heybonk.android"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-silver-900 dark:bg-white text-white dark:text-silver-900 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
                  </svg>
                  Google Play
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer navigation */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-silver-500 dark:text-silver-400">
          <Link to="/terms" className="hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors">
            Terms of Service
          </Link>
          <span className="hidden sm:inline">•</span>
          <Link to="/privacy" className="hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors">
            Privacy Policy
          </Link>
          <span className="hidden sm:inline">•</span>
          <Link to="/" className="hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors">
            Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default Support;
